/**
 * Platform-agnostic configuration for @nordex/core.
 * 
 * Usage:
 * - Web (Vite): Call setConfig() in main.tsx with import.meta.env values
 * - React Native: Call setConfig() in App.tsx with Config values
 * 
 * Example:
 * ```ts
 * import { setConfig } from '@nordex/core/config';
 * 
 * setConfig({
 *   apiBaseUrl: 'https://api.nordex.trade',
 *   wsBaseUrl: 'wss://api.nordex.trade',
 * });
 * ```
 */

export interface CoreConfig {
  /** Base URL for HTTP API (e.g., 'https://api.nordex.trade') */
  apiBaseUrl: string;
  /** Base URL for WebSocket connections (e.g., 'wss://api.nordex.trade') */
  wsBaseUrl: string;
  /** Enable debug logging */
  debug?: boolean;
  /** Function to get the current JWT auth token */
  getAuthToken?: () => string | null | Promise<string | null>;
}

// Default configuration
const DEFAULT_CONFIG: CoreConfig = {
  apiBaseUrl: "https://api.nordex.trade",
  wsBaseUrl: "wss://api.nordex.trade",
  debug: false,
};

// Module-level configuration
let config: CoreConfig = { ...DEFAULT_CONFIG };

/**
 * Set the configuration for @nordex/core.
 * Should be called once at app startup before using any API or WebSocket clients.
 */
export function setConfig(newConfig: Partial<CoreConfig>): void {
  config = { ...config, ...newConfig };
  
  if (config.debug) {
    console.log("[Core] Config updated:", config);
  }
}

/**
 * Get the current configuration.
 */
export function getConfig(): CoreConfig {
  return config;
}

/**
 * Get the API base URL.
 */
export function getApiBaseUrl(): string {
  return config.apiBaseUrl;
}

/**
 * Get the WebSocket base URL.
 */
export function getWsBaseUrl(): string {
  return config.wsBaseUrl;
}

/**
 * Check if debug mode is enabled.
 */
export function isDebugEnabled(): boolean {
  return config.debug ?? false;
}

/**
 * Get the auth token getter function.
 */
export function getAuthTokenGetter(): (() => string | null | Promise<string | null>) | undefined {
  return config.getAuthToken;
}

